import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, Users } from 'lucide-react';

/**
 * Admin → User Actions → View Downlines
 * Shows the selected user's Level-1 (direct) and Level-2 (indirect) referrals
 * grouped under each L1. Admin-activated (silent) accounts are flagged.
 *
 * Backing endpoint: GET /api/admin/users/{user_id}/downlines
 */
export function UserDownlinesDialog({ open, onOpenChange, user, api }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !user?.id) return;
    setLoading(true);
    setData(null);
    api.get(`/api/admin/users/${user.id}/downlines`)
      .then(({ data: res }) => setData(res))
      .catch(() => setData({ level_1: [], level_2: [], totals: { level_1: 0, level_2: 0 } }))
      .finally(() => setLoading(false));
  }, [open, user?.id, api]);

  const l1 = data?.level_1 || [];
  const l2 = data?.level_2 || [];
  const totalL1 = data?.totals?.level_1 ?? l1.length;
  const totalL2 = data?.totals?.level_2 ?? l2.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#041A3B] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="user-downlines-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-[#00E5FF]" />
            Downlines · {user?.name || user?.email}
          </DialogTitle>
          <p className="text-[10px] uppercase tracking-widest text-[#5C7599] mt-1">
            L1 · {totalL1} &nbsp;·&nbsp; L2 · {totalL2}
          </p>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#00E5FF] mx-auto" />
          </div>
        ) : l1.length === 0 ? (
          <div className="py-10 text-center">
            <Users className="w-10 h-10 text-[#5C7599] mx-auto mb-3 opacity-60" />
            <p className="text-[#A0B4CC] text-sm">No direct referrals yet.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-downlines-tree">
            <p className="text-[10px] uppercase tracking-widest text-[#00E5FF]">
              Level 1 · Direct referrals
            </p>
            {l1.map((ref, i) => {
              const isAdmin = !!ref.admin_activated;
              const children = l2.filter((c) => c.via_id === ref.id);
              return (
                <div key={ref.id || `l1-${i}`} data-testid={`admin-l1-row-${ref.id || i}`}>
                  <div className={`p-3 rounded-lg bg-[#000B18] border-l-4 ${isAdmin ? 'border-[#5C7599] opacity-60' : 'border-[#00E5FF]'}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className={`font-medium text-sm truncate ${isAdmin ? 'text-[#A0B4CC]' : 'text-white'}`}>
                          {ref.name || '(unnamed)'}
                        </p>
                        <p className="text-[#5C7599] text-[11px] truncate">{ref.email}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`px-2 py-0.5 rounded text-[10px] ${
                          ref.subscription_status === 'active' && !isAdmin
                            ? 'bg-[#00E676]/20 text-[#00E676]'
                            : 'bg-[#5C7599]/20 text-[#5C7599]'
                        }`}>
                          {ref.subscription_status || 'inactive'}
                        </span>
                        {isAdmin && (
                          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide bg-amber-400/10 text-amber-400 border border-amber-400/30">
                            No bonus
                          </span>
                        )}
                        {children.length > 0 && (
                          <span className="text-[9px] uppercase tracking-widest text-[#5C7599]">
                            +{children.length} L2
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {children.length > 0 && (
                    <div className="mt-2 ml-4 pl-3 border-l border-dashed border-white/10 space-y-2">
                      {children.map((c, ci) => {
                        const cAdmin = !!c.admin_activated;
                        return (
                          <div
                            key={c.id || `l2-${ci}`}
                            className={`p-2 rounded-md bg-[#000B18]/60 border-l-2 ${cAdmin ? 'border-[#5C7599]/50 opacity-60' : 'border-[#00E5FF]/40'}`}
                            data-testid={`admin-l2-row-${c.id || ci}`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className={`text-[13px] truncate ${cAdmin ? 'text-[#A0B4CC]' : 'text-white'}`}>
                                  {c.name || '(unnamed)'}
                                </p>
                                <p className="text-[#5C7599] text-[10px] truncate">{c.email}</p>
                              </div>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                c.subscription_status === 'active' && !cAdmin
                                  ? 'bg-[#00E676]/20 text-[#00E676]'
                                  : 'bg-[#5C7599]/20 text-[#5C7599]'
                              }`}>
                                {c.subscription_status || 'inactive'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default UserDownlinesDialog;
